export function formatPrice(value, locale = 'en-US', currency = 'Rs', compact = false) {
    let num = Number(value);
    if (isNaN(num)) num = 0;

    if (compact) {
        const formatter = new Intl.NumberFormat(locale, {
            notation: 'compact',
            compactDisplay: 'short',
            minimumFractionDigits: num < 10_000 ? 0 : 2,
            maximumFractionDigits: 2,
        });

        return `${currency} ${formatter.format(num)}`;
    }

    return `${currency} ${new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(num)}`;
}



export function formatNumber(value, locale = 'en-US', compact = false) {
    let num = Number(value);
    if (isNaN(num)) num = 0;

    if (compact) {
        const formatter = new Intl.NumberFormat(locale, {
            notation: 'compact',
            compactDisplay: 'short',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
        return formatter.format(num);
    }

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(num);
}




export function formatDuration(seconds) {
    // Ensure it's a number
    seconds = Number(seconds);
    if (isNaN(seconds)) return "Invalid time";

    if (seconds < 1) return `${seconds.toFixed(2)}s`; // show precise fraction

    const days = Math.floor(seconds / (3600 * 24));
    seconds %= 3600 * 24;

    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;

    const minutes = Math.floor(seconds / 60);
    seconds = seconds % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) {
        parts.push(`${seconds.toFixed(seconds % 1 === 0 ? 2 : 0)}s`);
    }

    return parts.join(" ");
}


export const getUserTimeZone = () => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return userTimeZone
}


// utils/DefaultFunction.js

/**
 * Check if a user has permission for a specific feature
 * @param {string} slug - The permission slug to check
 * @param {Array} permissions - Array of permission objects from API
 * @returns {boolean} - true if permission is granted, false otherwise
 */
export const hasPermission = (slug, permissions) => {
    // If no permissions data, default to false (restrictive)
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
        return false;
    }

    // Find the permission object
    const permission = permissions.find(p => p.slug === slug);

    // If permission object exists, check if it has explicit permission property
    // Otherwise, just finding it in the array means it's permitted
    if (permission) {
        // If permission property exists, use it; otherwise default to true (item exists = permitted)
        return permission.permission !== undefined ? permission.permission : true;
    }

    return false;
};

/**
 * Check multiple permissions at once
 * @param {Array<string>} slugs - Array of permission slugs to check
 * @param {Array} permissions - Array of permission objects from API
 * @returns {Object} - Object with slug as key and boolean as value
 */
export const checkMultiplePermissions = (slugs, permissions) => {
    const result = {};
    slugs.forEach(slug => {
        result[slug] = hasPermission(slug, permissions);
    });
    return result;
};

/**
 * Filter items based on permissions
 * @param {Array} items - Array of items with 'slug' property
 * @param {Array} permissions - Array of permission objects from API
 * @returns {Array} - Filtered array containing only items with permission
 */
export const filterByPermission = (items, permissions) => {
    // If no permissions data, return empty array (restrictive)
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
        return [];
    }

    return items.filter(item => hasPermission(item.slug, permissions));
};

/**
 * Check if user has ALL specified permissions
 * @param {Array<string>} slugs - Array of permission slugs
 * @param {Array} permissions - Array of permission objects from API
 * @returns {boolean} - true only if ALL permissions are granted
 */
export const hasAllPermissions = (slugs, permissions) => {
    return slugs.every(slug => hasPermission(slug, permissions));
};

/**
 * Check if user has ANY of the specified permissions
 * @param {Array<string>} slugs - Array of permission slugs
 * @param {Array} permissions - Array of permission objects from API
 * @returns {boolean} - true if ANY permission is granted
 */
export const hasAnyPermission = (slugs, permissions) => {
    return slugs.some(slug => hasPermission(slug, permissions));
};

/**
 * Get all enabled permissions
 * @param {Array} permissions - Array of permission objects from API
 * @returns {Array<string>} - Array of enabled permission slugs
 */
export const getEnabledPermissions = (permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
        return [];
    }

    return permissions
        .filter(p => {
            // If permission property exists, check it; otherwise presence = enabled
            return p.permission !== undefined ? p.permission : true;
        })
        .map(p => p.slug);
};

/**
 * Get all disabled permissions
 * @param {Array} permissions - Array of permission objects from API
 * @returns {Array<string>} - Array of disabled permission slugs
 */
export const getDisabledPermissions = (permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
        return [];
    }

    return permissions
        .filter(p => {
            // If permission property exists, check it; otherwise presence = enabled
            return p.permission !== undefined ? !p.permission : false;
        })
        .map(p => p.slug);
};

/**
 * Get permission status with details
 * @param {string} slug - The permission slug to check
 * @param {Array} permissions - Array of permission objects from API
 * @returns {Object|null} - Full permission object or null if not found
 */
export const getPermissionDetails = (slug, permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
        return null;
    }
    return permissions.find(p => p.slug === slug) || null;
};


export const getApiHeaders = () => ({
    'x-api-key': localStorage.getItem('api-key'),
    'x-access-token': localStorage.getItem('customer-portal'),
});

export const COUNTRY_CODES = [
    { name: 'Afghanistan', code: '+93', iso: 'AF' },
    { name: 'Albania', code: '+355', iso: 'AL' },
    { name: 'Algeria', code: '+213', iso: 'DZ' },
    { name: 'Andorra', code: '+376', iso: 'AD' },
    { name: 'Angola', code: '+244', iso: 'AO' },
    { name: 'Argentina', code: '+54', iso: 'AR' },
    { name: 'Armenia', code: '+374', iso: 'AM' },
    { name: 'Australia', code: '+61', iso: 'AU' },
    { name: 'Austria', code: '+43', iso: 'AT' },
    { name: 'Azerbaijan', code: '+994', iso: 'AZ' },
    { name: 'Bahamas', code: '+1-242', iso: 'BS' },
    { name: 'Bahrain', code: '+973', iso: 'BH' },
    { name: 'Bangladesh', code: '+880', iso: 'BD' },
    { name: 'Barbados', code: '+1-246', iso: 'BB' },
    { name: 'Belarus', code: '+375', iso: 'BY' },
    { name: 'Belgium', code: '+32', iso: 'BE' },
    { name: 'Belize', code: '+501', iso: 'BZ' },
    { name: 'Benin', code: '+229', iso: 'BJ' },
    { name: 'Bhutan', code: '+975', iso: 'BT' },
    { name: 'Bolivia', code: '+591', iso: 'BO' },
    { name: 'Bosnia & Herzegovina', code: '+387', iso: 'BA' },
    { name: 'Botswana', code: '+267', iso: 'BW' },
    { name: 'Brazil', code: '+55', iso: 'BR' },
    { name: 'Brunei', code: '+673', iso: 'BN' },
    { name: 'Bulgaria', code: '+359', iso: 'BG' },
    { name: 'Cambodia', code: '+855', iso: 'KH' },
    { name: 'Cameroon', code: '+237', iso: 'CM' },
    { name: 'Canada', code: '+1', iso: 'CA' },
    { name: 'Cape Verde', code: '+238', iso: 'CV' },
    { name: 'Chile', code: '+56', iso: 'CL' },
    { name: 'Colombia', code: '+57', iso: 'CO' },
    { name: 'Costa Rica', code: '+506', iso: 'CR' },
    { name: 'Croatia', code: '+385', iso: 'HR' },
    { name: 'Cuba', code: '+53', iso: 'CU' },
    { name: 'Cyprus', code: '+357', iso: 'CY' },
    { name: 'Czech Republic', code: '+420', iso: 'CZ' },
    { name: 'Denmark', code: '+45', iso: 'DK' },
    { name: 'Dominica', code: '+1-767', iso: 'DM' },
    { name: 'Dominican Republic', code: '+1-809', iso: 'DO' },
    { name: 'Ecuador', code: '+593', iso: 'EC' },
    { name: 'Egypt', code: '+20', iso: 'EG' },
    { name: 'El Salvador', code: '+503', iso: 'SV' },
    { name: 'Estonia', code: '+372', iso: 'EE' },
    { name: 'Eswatini', code: '+268', iso: 'SZ' },
    { name: 'Ethiopia', code: '+251', iso: 'ET' },
    { name: 'Fiji', code: '+679', iso: 'FJ' },
    { name: 'Finland', code: '+358', iso: 'FI' },
    { name: 'France', code: '+33', iso: 'FR' },
    { name: 'Gabon', code: '+241', iso: 'GA' },
    { name: 'Gambia', code: '+220', iso: 'GM' },
    { name: 'Georgia', code: '+995', iso: 'GE' },
    { name: 'Germany', code: '+49', iso: 'DE' },
    { name: 'Ghana', code: '+233', iso: 'GH' },
    { name: 'Greece', code: '+30', iso: 'GR' },
    { name: 'Grenada', code: '+1-473', iso: 'GD' },
    { name: 'Guatemala', code: '+502', iso: 'GT' },
    { name: 'Guinea', code: '+224', iso: 'GN' },
    { name: 'Guyana', code: '+592', iso: 'GY' },
    { name: 'Haiti', code: '+509', iso: 'HT' },
    { name: 'Honduras', code: '+504', iso: 'HN' },
    { name: 'Hungary', code: '+36', iso: 'HU' },
    { name: 'Iceland', code: '+354', iso: 'IS' },
    { name: 'India', code: '+91', iso: 'IN' },
    { name: 'Indonesia', code: '+62', iso: 'ID' },
    { name: 'Iran', code: '+98', iso: 'IR' },
    { name: 'Iraq', code: '+964', iso: 'IQ' },
    { name: 'Ireland', code: '+353', iso: 'IE' },
    { name: 'Israel', code: '+972', iso: 'IL' },
    { name: 'Italy', code: '+39', iso: 'IT' },
    { name: 'Jamaica', code: '+1-876', iso: 'JM' },
    { name: 'Japan', code: '+81', iso: 'JP' },
    { name: 'Jordan', code: '+962', iso: 'JO' },
    { name: 'Kazakhstan', code: '+7', iso: 'KZ' },
    { name: 'Kenya', code: '+254', iso: 'KE' },
    { name: 'Kuwait', code: '+965', iso: 'KW' },
    { name: 'Kyrgyzstan', code: '+996', iso: 'KG' },
    { name: 'Laos', code: '+856', iso: 'LA' },
    { name: 'Latvia', code: '+371', iso: 'LV' },
    { name: 'Lebanon', code: '+961', iso: 'LB' },
    { name: 'Lesotho', code: '+266', iso: 'LS' },
    { name: 'Liberia', code: '+231', iso: 'LR' },
    { name: 'Libya', code: '+218', iso: 'LY' },
    { name: 'Lithuania', code: '+370', iso: 'LT' },
    { name: 'Luxembourg', code: '+352', iso: 'LU' },
    { name: 'Madagascar', code: '+261', iso: 'MG' },
    { name: 'Malawi', code: '+265', iso: 'MW' },
    { name: 'Malaysia', code: '+60', iso: 'MY' },
    { name: 'Maldives', code: '+960', iso: 'MV' },
    { name: 'Mali', code: '+223', iso: 'ML' },
    { name: 'Malta', code: '+356', iso: 'MT' },
    { name: 'Mauritius', code: '+230', iso: 'MU' },
    { name: 'Mexico', code: '+52', iso: 'MX' },
    { name: 'Moldova', code: '+373', iso: 'MD' },
    { name: 'Monaco', code: '+377', iso: 'MC' },
    { name: 'Mongolia', code: '+976', iso: 'MN' },
    { name: 'Montenegro', code: '+382', iso: 'ME' },
    { name: 'Morocco', code: '+212', iso: 'MA' },
    { name: 'Mozambique', code: '+258', iso: 'MZ' },
    { name: 'Myanmar', code: '+95', iso: 'MM' },
    { name: 'Namibia', code: '+264', iso: 'NA' },
    { name: 'Nepal', code: '+977', iso: 'NP' },
    { name: 'Netherlands', code: '+31', iso: 'NL' },
    { name: 'New Zealand', code: '+64', iso: 'NZ' },
    { name: 'Nicaragua', code: '+505', iso: 'NI' },
    { name: 'Niger', code: '+227', iso: 'NE' },
    { name: 'Nigeria', code: '+234', iso: 'NG' },
    { name: 'North Macedonia', code: '+389', iso: 'MK' },
    { name: 'Norway', code: '+47', iso: 'NO' },
    { name: 'Oman', code: '+968', iso: 'OM' },
    { name: 'Pakistan', code: '+92', iso: 'PK' },
    { name: 'Panama', code: '+507', iso: 'PA' },
    { name: 'Papua New Guinea', code: '+675', iso: 'PG' },
    { name: 'Paraguay', code: '+595', iso: 'PY' },
    { name: 'Peru', code: '+51', iso: 'PE' },
    { name: 'Philippines', code: '+63', iso: 'PH' },
    { name: 'Poland', code: '+48', iso: 'PL' },
    { name: 'Portugal', code: '+351', iso: 'PT' },
    { name: 'Qatar', code: '+974', iso: 'QA' },
    { name: 'Romania', code: '+40', iso: 'RO' },
    { name: 'Russia', code: '+7', iso: 'RU' },
    { name: 'Rwanda', code: '+250', iso: 'RW' },
    { name: 'Saint Kitts & Nevis', code: '+1-869', iso: 'KN' },
    { name: 'Saint Lucia', code: '+1-758', iso: 'LC' },
    { name: 'Saint Vincent & Grenadines', code: '+1-784', iso: 'VC' },
    { name: 'Samoa', code: '+685', iso: 'WS' },
    { name: 'San Marino', code: '+378', iso: 'SM' },
    { name: 'Saudi Arabia', code: '+966', iso: 'SA' },
    { name: 'Senegal', code: '+221', iso: 'SN' },
    { name: 'Serbia', code: '+381', iso: 'RS' },
    { name: 'Seychelles', code: '+248', iso: 'SC' },
    { name: 'Sierra Leone', code: '+232', iso: 'SL' },
    { name: 'Singapore', code: '+65', iso: 'SG' },
    { name: 'Slovakia', code: '+421', iso: 'SK' },
    { name: 'Slovenia', code: '+386', iso: 'SI' },
    { name: 'Solomon Islands', code: '+677', iso: 'SB' },
    { name: 'Somalia', code: '+252', iso: 'SO' },
    { name: 'South Africa', code: '+27', iso: 'ZA' },
    { name: 'South Korea', code: '+82', iso: 'KR' },
    { name: 'South Sudan', code: '+211', iso: 'SS' },
    { name: 'Spain', code: '+34', iso: 'ES' },
    { name: 'Sri Lanka', code: '+94', iso: 'LK' },
    { name: 'Sudan', code: '+249', iso: 'SD' },
    { name: 'Suriname', code: '+597', iso: 'SR' },
    { name: 'Sweden', code: '+46', iso: 'SE' },
    { name: 'Switzerland', code: '+41', iso: 'CH' },
    { name: 'Syria', code: '+963', iso: 'SY' },
    { name: 'Taiwan', code: '+886', iso: 'TW' },
    { name: 'Tajikistan', code: '+992', iso: 'TJ' },
    { name: 'Tanzania', code: '+255', iso: 'TZ' },
    { name: 'Thailand', code: '+66', iso: 'TH' },
    { name: 'Togo', code: '+228', iso: 'TG' },
    { name: 'Tonga', code: '+676', iso: 'TO' },
    { name: 'Trinidad & Tobago', code: '+1-868', iso: 'TT' },
    { name: 'Tunisia', code: '+216', iso: 'TN' },
    { name: 'Turkey', code: '+90', iso: 'TR' },
    { name: 'Turkmenistan', code: '+993', iso: 'TM' },
    { name: 'Tuvalu', code: '+688', iso: 'TV' },
    { name: 'Uganda', code: '+256', iso: 'UG' },
    { name: 'Ukraine', code: '+380', iso: 'UA' },
    { name: 'United Arab Emirates', code: '+971', iso: 'AE' },
    { name: 'United Kingdom', code: '+44', iso: 'GB' },
    { name: 'United States', code: '+1', iso: 'US' },
    { name: 'Uruguay', code: '+598', iso: 'UY' },
    { name: 'Uzbekistan', code: '+998', iso: 'UZ' },
    { name: 'Vanuatu', code: '+678', iso: 'VU' },
    { name: 'Vatican City', code: '+379', iso: 'VA' },
    { name: 'Venezuela', code: '+58', iso: 'VE' },
    { name: 'Vietnam', code: '+84', iso: 'VN' },
    { name: 'Yemen', code: '+967', iso: 'YE' },
    { name: 'Zambia', code: '+260', iso: 'ZM' },
    { name: 'Zimbabwe', code: '+263', iso: 'ZW' },
];

export const languageOptions = [
  { value: "af", label: "Afrikaans" },
  { value: "sq", label: "Albanian" },
  { value: "ar", label: "Arabic" },
  { value: "ar_EG", label: "Arabic (EGY)" },
  { value: "ar_AE", label: "Arabic (UAE)" },
  { value: "ar_LB", label: "Arabic (LBN)" },
  { value: "ar_MA", label: "Arabic (MAR)" },
  { value: "ar_QA", label: "Arabic (QAT)" },
  { value: "az", label: "Azerbaijani" },
  { value: "be_BY", label: "Belarusian" },
  { value: "bn", label: "Bengali" },
  { value: "bn_IN", label: "Bengali (IND)" },
  { value: "bg", label: "Bulgarian" },
  { value: "ca", label: "Catalan" },
  { value: "zh_CN", label: "Chinese (CHN)" },
  { value: "zh_HK", label: "Chinese (HKG)" },
  { value: "zh_TW", label: "Chinese (TAI)" },
  { value: "hr", label: "Croatian" },
  { value: "cs", label: "Czech" },
  { value: "da", label: "Danish" },
  { value: "prs_AF", label: "Dari" },
  { value: "nl", label: "Dutch" },
  { value: "nl_BE", label: "Dutch (BEL)" },
  { value: "en", label: "English" },
  { value: "en_GB", label: "English (UK)" },
  { value: "en_US", label: "English (US)" },
  { value: "en_AE", label: "English (UAE)" },
  { value: "en_AU", label: "English (AUS)" },
  { value: "en_CA", label: "English (CAN)" },
  { value: "en_GH", label: "English (GHA)" },
  { value: "en_IE", label: "English (IRL)" },
  { value: "en_IN", label: "English (IND)" },
  { value: "en_JM", label: "English (JAM)" },
  { value: "en_MY", label: "English (MYS)" },
  { value: "en_NZ", label: "English (NZL)" },
  { value: "en_QA", label: "English (QAT)" },
  { value: "en_SG", label: "English (SGP)" },
  { value: "en_UG", label: "English (UGA)" },
  { value: "en_ZA", label: "English (ZAF)" },
  { value: "et", label: "Estonian" },
  { value: "fil", label: "Filipino" },
  { value: "fi", label: "Finnish" },
  { value: "fr", label: "French" },
  { value: "fr_BE", label: "French (BEL)" },
  { value: "fr_CA", label: "French (CAN)" },
  { value: "fr_CH", label: "French (CHE)" },
  { value: "fr_CI", label: "French (CIV)" },
  { value: "fr_MA", label: "French (MAR)" },
  { value: "ka", label: "Georgian" },
  { value: "de", label: "German" },
  { value: "de_AT", label: "German (AUT)" },
  { value: "de_CH", label: "German (CHE)" },
  { value: "el", label: "Greek" },
  { value: "gu", label: "Gujarati" },
  { value: "ha", label: "Hausa" },
  { value: "he", label: "Hebrew" },
  { value: "hi", label: "Hindi" },
  { value: "hu", label: "Hungarian" },
  { value: "id", label: "Indonesian" },
  { value: "ga", label: "Irish" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "kn", label: "Kannada" },
  { value: "kk", label: "Kazakh" },
  { value: "rw_RW", label: "Kinyarwanda" },
  { value: "ko", label: "Korean" },
  { value: "ky_KG", label: "Kyrgyz (Kyrgyzstan)" },
  { value: "lo", label: "Lao" },
  { value: "lv", label: "Latvian" },
  { value: "lt", label: "Lithuanian" },
  { value: "mk", label: "Macedonian" },
  { value: "ms", label: "Malay" },
  { value: "ml", label: "Malayalam" },
  { value: "mr", label: "Marathi" },
  { value: "nb", label: "Norwegian" },
  { value: "ps_AF", label: "Pashto" },
  { value: "fa", label: "Persian" },
  { value: "pl", label: "Polish" },
  { value: "pt_BR", label: "Portuguese (BR)" },
  { value: "pt_PT", label: "Portuguese (POR)" },
  { value: "pa", label: "Punjabi" },
  { value: "ro", label: "Romanian" },
  { value: "ru", label: "Russian" },
  { value: "sr", label: "Serbian" },
  { value: "si_LK", label: "Sinhala" },
  { value: "sk", label: "Slovak" },
  { value: "sl", label: "Slovenian" },
  { value: "es", label: "Spanish" },
  { value: "es_AR", label: "Spanish (ARG)" },
  { value: "es_CL", label: "Spanish (CHL)" },
  { value: "es_CO", label: "Spanish (COL)" },
  { value: "es_CR", label: "Spanish (CRI)" },
  { value: "es_DO", label: "Spanish (DOM)" },
  { value: "es_EC", label: "Spanish (ECU)" },
  { value: "es_HN", label: "Spanish (HND)" },
  { value: "es_MX", label: "Spanish (MEX)" },
  { value: "es_PA", label: "Spanish (PAN)" },
  { value: "es_PE", label: "Spanish (PER)" },
  { value: "es_ES", label: "Spanish (SPA)" },
  { value: "es_UY", label: "Spanish (URY)" },
  { value: "sw", label: "Swahili" },
  { value: "sv", label: "Swedish" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "th", label: "Thai" },
  { value: "tr", label: "Turkish" },
  { value: "uk", label: "Ukrainian" },
  { value: "ur", label: "Urdu" },
  { value: "uz", label: "Uzbek" },
  { value: "vi", label: "Vietnamese" },
  { value: "zu", label: "Zulu" },
];

export const MESSAGE_EXPIRY_OPTIONS = {
    // templateType '1' = Authentication
    '1': [
        { value: '30', label: '30 Seconds' },
        { value: '60', label: '1 Minute' },
        { value: '120', label: '2 Minutes' },
        { value: '180', label: '3 Minutes' },
        { value: '300', label: '5 Minutes' },
        { value: '600', label: '10 Minutes' },
        { value: '900', label: '15 Minutes' },
    ],
    // templateType '2' = Transaction / Informational
    '2': [
        { value: '30', label: '30 Seconds' },
        { value: '60', label: '1 Minute' },
        { value: '120', label: '2 Minutes' },
        { value: '300', label: '5 Minutes' },
        { value: '600', label: '10 Minutes' },
        { value: '900', label: '15 Minutes' },
        { value: '1800', label: '30 Minutes' },
        { value: '3600', label: '1 Hour' },
        { value: '10800', label: '3 Hours' },
        { value: '21600', label: '6 Hours' },
        { value: '43200', label: '12 Hours' },
    ],
    '4': [
        { value: '30', label: '30 Seconds' },
        { value: '60', label: '1 Minute' },
        { value: '120', label: '2 Minutes' },
        { value: '300', label: '5 Minutes' },
        { value: '600', label: '10 Minutes' },
        { value: '900', label: '15 Minutes' },
        { value: '1800', label: '30 Minutes' },
        { value: '3600', label: '1 Hour' },
        { value: '10800', label: '3 Hours' },
        { value: '21600', label: '6 Hours' },
        { value: '43200', label: '12 Hours' },
    ],
    // templateType '3' = Marketing
    '3': [
        { value: '43200', label: '12 Hours' },
        { value: '86400', label: '24 Hours' },
        { value: '172800', label: '48 Hours' },
        { value: '259200', label: '72 Hours' },
        { value: '345600', label: '96 Hours' },
        { value: '604800', label: '168 Hours (7 Days)' },
    ],
};